import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import {
  Formik,
  Form,
  FieldArray,
  useField,
} from 'formik';
import * as actions from 'hellospa/page/prep-and-send/data/actions';
import { trackHeapCustomEvent } from 'common/utils/heap';

import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import * as types from 'hellospa/page/prep-and-send/data/types';
import {
  ConnectFormikToStepper,
  BannerErrorMessage,
  AutoSave,
} from 'hellospa/components/prep-and-send/formik-utils';
import { PageHeader, SectionHeader } from '../header';
import styles from './index.scss';
import messages from './intl';
import * as Yup from 'yup';
import { MeOnlyCta, AddRecipientBtn, AddMeBtn, BulkSendButton } from './buttons';
import RecipientOrder from './RecipientOrder';
import RecipientRows from './RecipientRows';
import RecipientReassignment from './RecipientReassignment';
import QESToggle from './QESToggle';
import { useConvertDeepIntegrationFile } from 'hellospa/components/deep-integration/custom-hooks';

/**
 * I had to turn off enableReinitialize for autosave, but that broke bulk send.
 * This component was needed to synchronize signerFile from Redux with signerFile in Formik
 */
function SyncFormikValue({ name, value }: { name: string, value: unknown }) {
  const [fieldProps, , helpers] = useField(name);

  React.useEffect(() => {
    if (fieldProps.value !== value) {
      helpers.setValue(value);
    }
  }, [helpers, fieldProps.value, value]);

  return null;
}

const Recipient: types.WorkflowComponent = ({ onSaveActions, formRef }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const workflow = useSelector(selectors.getWorkflow);
  const allowTemplatePreview = useSelector(selectors.allowTemplatePreview);
  const showTemplatePreview = allowTemplatePreview
    && workflow.tags.includes(types.WorkflowTags.SignatureRequestTemplate)
    && !workflow.tags.includes(types.WorkflowTags.SignatureRequestDocument);
  const recipientReassignment = useSelector(selectors.isRecipientReassignmentEnabled);
  const recipientOrder = useSelector(selectors.isRecipientOrderEnabled);
  const addRecipientAction = useSelector(selectors.getRecipientGenerator);
  const isBulkSend = useSelector(selectors.isBulkSend);
  let recipients = useSelector(selectors.getRecipientsForRequest);
  const signerFile = useSelector(selectors.getBulkSendData).signerFile;
  const isSendingTemplate = useSelector(selectors.isSendingTemplate);
  const isTemplateRequest = useSelector(selectors.isTemplateRequest);
  const qes = useSelector(selectors.getQES);

  // Show or hide section header depending on if there are options
  const allowRecipientReassignment = useSelector(selectors.allowRecipientReassignment);
  const allowRecipientOrder = useSelector(selectors.allowRecipientOrder);
  const allowQES = useSelector(selectors.allowQES);

  const baseSchema: Yup.ObjectSchema<InitialValues> | null = useSelector(
    selectors.getRecipientValidationSchema,
  );

  const schema = React.useMemo(() => {
    return baseSchema;
  }, [baseSchema]);

  const defaultRecipients = React.useMemo(() => {
    if (addRecipientAction) {
      return [addRecipientAction()];
    }

    return null;
  }, [addRecipientAction]);

  type InitialValues = {
    recipients: typeof recipients,
    recipientReassignment: typeof recipientReassignment,
    recipientOrder: typeof recipientOrder,
    qes: boolean,
    signerFile: typeof signerFile,
  };

  const normalizeValues = React.useCallback((values: InitialValues) => {

    const normalizedValues: InitialValues = schema ? schema.cast(values) : values;

    if (isSendingTemplate) {
      const recipients = values.recipients.map((recipient) => {
        const normalizedRecipient = normalizedValues.recipients.find(
          (n) => n.id === recipient.id,
        );

        // If a normalizedRecipient can't be found it should be because it was
        // left empty and is an optional role.
        return normalizedRecipient || recipient;
      });

      return {
        ...values,
        recipients,
      };
    }

    return normalizedValues;

  }, [isSendingTemplate, schema]);

  const saveToRedux = React.useCallback(async (v: InitialValues) => {
    const values = normalizeValues(v);
    await dispatch(actions.resetRecipients(values.recipients));

    // don't update the settings when sending templates because
    // these settings are part of the template and cannot be
    // changed for the specific request
    if (!isSendingTemplate) {
      await dispatch(actions.setSettings({
        recipientReassignment: values.recipientReassignment,
        recipientOrder: values.recipientOrder,
        qes: values.qes,
      }));
    } else if (allowQES) {
      await dispatch(actions.setSettings({
        qes: values.qes,
      }));
    }
  }, [dispatch, isSendingTemplate, allowQES, normalizeValues]);

  const submit = React.useCallback(async (values: InitialValues) => {
    const total = values.recipients.reduce((total, recipient) => {
      if (recipient.type === types.RecipientTypes.Signer
        || recipient.type === types.RecipientTypes.Role) {
        return total + recipient.attachments.length;
      }
      return total;
    }, 0);
    trackHeapCustomEvent('Added Signers', { 'Signer Count': values.recipients.length });
    trackHeapCustomEvent('Added Attachments', { 'Attachment Count': total });
    await saveToRedux(values);
    await dispatch(actions.workflowOnSave(onSaveActions));
    if (showTemplatePreview && !process.env.IS_STORYBOOK) {
      // Preview data (editorFields: { fields, rules, pages }) are not included
      // in use-template, so it needs to be fetched before moving to the review
      // page.
      // I considered trying to do this as you leave the documents page, but the
      // workflow may not be known before leaving that page. That means I can't
      // use the same showTemplatePreview logic as this file has.
      await dispatch(actions.refreshPreviewData());
    }
  }, [dispatch, onSaveActions, saveToRedux, showTemplatePreview]);

  // mimicking componentDidMount
  React.useEffect(() => {
    if (isTemplateRequest) {
      trackHeapCustomEvent('template_add_roles');
    } else {
      trackHeapCustomEvent('sign_or_send_add_signers');
    }
  }, [isTemplateRequest]);

  useConvertDeepIntegrationFile();

  if (!recipients.length && !isBulkSend) {
    if (defaultRecipients) {
      recipients = defaultRecipients;
    } else {
      return null;
    }
  }

  if (!schema) {
    return null;
  }

  const initialValues: InitialValues = {
    recipients,
    recipientReassignment,
    recipientOrder,
    qes,
    signerFile,
  };

  /**
   * This function bypasses Formik's submit and just moves to the next page. An
   * earlier implementation used Formik to set the recipients to an empty array,
   * but in addition to looking weird to have all recipients disappear it
   * doesn't pass validation.
   */
  const handleMeOnly = async () => {
    return submit({
      recipients: [],
      recipientReassignment: false,
      recipientOrder: false,
      qes: false,
      signerFile: null,
    });
  };


  let sectionHeader = messages.headerAddSigners;
  if (isBulkSend) {
    sectionHeader = messages.headerUploadCSV;
  } else if (isTemplateRequest) {
    sectionHeader = messages.headerAddSignerRoles;
  }

  return (
    <div data-qa-ref="prep-and-send-recipients">
      <PageHeader
        dataQaRef="page-header"
        text={intl.formatMessage(messages.headerSignersPage)} />
      <Formik
        initialValues={initialValues}
        onSubmit={async (values) => submit(values)}
        validateOnChange={false}
        validationSchema={schema}
        validateOnMount={true}
        enableReinitialize={
          // This needs to be false for <AutoSave to work
          false
        }
        innerRef={types.useFormikRef(formRef)}
        // eslint-disable-next-line react/no-children-prop
        children={({ values, errors }) => (

          <Form>
            <div className={styles.sectionHeaderRow}>
              <SectionHeader text={intl.formatMessage(sectionHeader)} />
              <MeOnlyCta handleClick={handleMeOnly} recipients={values.recipients} />
              {/*
                Temporarily keep this button while the experiment for
                the core HS link is active (above).
                When removing, be sure to also remove the style selectors.
                <hr />
                <MeOnlyBtn onMeOnly={handleMeOnly} recipients={values.recipients} />
              */}
            </div>
            <ConnectFormikToStepper />
            <AutoSave
              values={values}
              errors={errors}
              saveToRedux={saveToRedux}
              reduxSaveAction={actions.save} />
            <SyncFormikValue name="signerFile" value={signerFile} />

            <BannerErrorMessage name="recipients" />
            <BannerErrorMessage name="signerFile.error" requireTouched={false} />
            <BulkSendButton signerFile={values.signerFile} />

            {!isBulkSend && (<FieldArray name="recipients" render={(arrayHelpers) => (
              <React.Fragment>
                <div className={styles.recipientRows}>
                  <RecipientRows helper={arrayHelpers} />
                </div>

                <div className={styles.addSignerContainer}>
                  <div>
                    <AddRecipientBtn helper={arrayHelpers} />
                  </div>
                  <div>
                    <AddMeBtn helper={arrayHelpers} />
                  </div>
                </div>
              </React.Fragment>
            )} />)}

            {
              (allowRecipientReassignment
                || allowQES
                || (allowRecipientOrder && values.recipients.length > 1)
              )
              && <div id="recipient-signer-settings-header" className={styles.ssHeader}>
                <FormattedMessage id='' description='Signer settings section header' defaultMessage='Signer Settings' />
              </div>
            }
            <div id="recipient-signer-settings">
              <RecipientReassignment />
              <RecipientOrder />
              <QESToggle />
            </div>
          </Form>
        )} />
    </div>
  );
};

Recipient.getText = (workflow) => {
  const msg = defineMessages({
    title: {
      id: '',
      description: 'title of page, also used in Stepper tab',
      defaultMessage: 'Add signers',
    },
    titleRoles: {
      id: '',
      description: 'title of page, also used in Stepper tab',
      defaultMessage: 'Add roles',
    },
    next: {
      id: '',
      description: 'text in next button',
      defaultMessage: 'Next',
    },
  });

  if (workflow.tags.includes(types.WorkflowTags.CreateTemplate)
    || workflow.tags.includes(types.WorkflowTags.CreateTemplateLink)
  ) {
    return {
      ...msg,
      title: msg.titleRoles,
    };
  }

  return msg;
};

export default Recipient;
